import api, { getApiErrors } from '@app/services/api';
import type {
    AITextAgentAddParams,
    AITextAgentAddResponse,
    AITextAgentDeleteParams,
    AITextAgentDeleteResponse,
    AITextAgentEditParams,
    AITextAgentEditResponse,
    AITextAgentListParams,
    ApiError,
} from '@app/services/api.types';
import { fn } from '@app/utils';
import { apiEndpoints, defaultErrorMessage } from '@app/utils/constants';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import useApp from '../useApp';

const buildTextAgentPayload = (params: AITextAgentAddParams | AITextAgentEditParams) => {
    const formData = new FormData();
    Object.entries(params).forEach(([key, value]) => {
        if (key === 'logo') {
            if (value instanceof File) {
                formData.append(key, value);
            }
            return;
        }
        if (value !== undefined) {
            formData.append(key, value as string | Blob);
        }
    });
    return formData;
};

export const createTextAgent = async (
    params: AITextAgentAddParams
): Promise<AITextAgentAddResponse> => {
    const url = apiEndpoints.aiTextAgents.add;
    const payload = buildTextAgentPayload(params);
    const response = await api.post(url, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response?.data?.data;
};

export const updateTextAgent = async (
    params: AITextAgentEditParams
): Promise<AITextAgentEditResponse> => {
    const url = apiEndpoints.aiTextAgents.update(params.id);
    const payload = buildTextAgentPayload(params);
    const response = await api.put(url, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response?.data?.data;
};

export const removeTextAgent = async (
    params: AITextAgentDeleteParams
): Promise<AITextAgentDeleteResponse> => {
    const url = apiEndpoints.aiTextAgents.delete(params.id);
    const response = await api.delete(url);
    return response?.data?.data;
};

export const exportTextAgents = async (params: AITextAgentListParams) => {
    const url = apiEndpoints.aiTextAgents.getListExport(params);
    const response = await api.get<Blob>(url, {
        responseType: 'blob',
    });
    return response;
};

const useTextAgentActions = () => {
    const { message } = useApp();

    const addTextAgent = useMutation<
        AITextAgentAddResponse,
        AxiosError<ApiError>,
        AITextAgentAddParams
    >({
        mutationFn: createTextAgent,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'Text agent added successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [['ai-text-agent', 'list']],
        },
    });

    const editTextAgent = useMutation<
        AITextAgentEditResponse,
        AxiosError<ApiError>,
        AITextAgentEditParams
    >({
        mutationFn: updateTextAgent,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'Text agent updated successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [['ai-text-agent', 'list']],
        },
    });

    const deleteTextAgent = useMutation<
        AITextAgentDeleteResponse,
        AxiosError<ApiError>,
        AITextAgentDeleteParams
    >({
        mutationFn: removeTextAgent,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'Text agent deleted successfully',
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
        meta: {
            invalidates: [['ai-text-agent', 'list']],
        },
    });

    const exportTextAgentsMutation = useMutation<
        AxiosResponse<Blob>,
        AxiosError<ApiError>,
        AITextAgentListParams
    >({
        mutationFn: exportTextAgents,
        onSuccess: (res) => {
            const match = res.headers['content-disposition'].match(/filename\s*=\s*"(.+)"/i);
            const filename = match?.[1] || 'text-agents.csv';
            fn.downloadFileFromBlob({
                blob: res.data,
                fileName: filename,
            });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.data, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
    });

    return {
        addTextAgent,
        editTextAgent,
        deleteTextAgent,
        exportTextAgents: exportTextAgentsMutation,
    };
};

export default useTextAgentActions;
